import React from "react";
import ReactDOM from "react-dom/client";
import "bootstrap/dist/css/bootstrap.min.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { BrowserRouter } from "react-router-dom";
import "./variables.scss";
import "./index.css";
import TemplateDataContext, {
  TemplateDataContextProvider,
} from "./Contexts/TemplateData.context";

// CRITICAL CSS INJECTION - Inject essential label hiding styles immediately
// This prevents any flash of labels by applying styles before React even starts
const criticalCSS = `
<style id="critical-label-hiding">
/* CRITICAL: Immediate label hiding to prevent flash */
.form-label.d-inline:not(.question-label):not(.input-radio-label):not(.input-checkbox-label) {
  opacity: 0 !important;
  height: 0 !important;
  margin: 0 !important;
  padding: 0 !important;
  overflow: hidden !important;
  visibility: hidden !important;
  transition: none !important;
}

/* Force hide known simple field patterns immediately */
.form-label.d-inline[data-label*="Name"],
.form-label.d-inline[data-label*="Email"],
.form-label.d-inline[data-label*="Phone"],
.form-label.d-inline[data-label*="Address"],
.form-label.d-inline[data-label*="City"],
.form-label.d-inline[data-label*="State"],
.form-label.d-inline[data-label*="Zip"],
.form-label.d-inline[data-label*="Date"] {
  opacity: 0 !important;
  height: 0 !important;
  margin: 0 !important;
  padding: 0 !important;
  overflow: hidden !important;
  visibility: hidden !important;
  display: none !important;
  transition: none !important;
}

/* Restore visibility for question labels */
.question-label {
  opacity: 1 !important;
  height: auto !important;
  margin: revert !important;
  padding: revert !important;
  overflow: visible !important;
  visibility: visible !important;
  display: inline-block !important;
}
</style>
`;

// Inject critical CSS immediately into document head
if (typeof document !== "undefined") {
  document.head.insertAdjacentHTML("beforeend", criticalCSS);
}

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);
root.render(
  <BrowserRouter>
    <TemplateDataContextProvider>
      <TemplateDataContext.Consumer>
        {(context) => <App />}
      </TemplateDataContext.Consumer>
    </TemplateDataContextProvider>
  </BrowserRouter>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();

// SEAMLESS label hiding - immediate and comprehensive approach
// This script prevents any flash of labels by running as early as possible
// and using multiple strategies to ensure complete coverage

// Critical: Run immediately when DOM is ready, before React renders
function criticalLabelHiding() {
  const isSimpleFieldText = (text: string): boolean => {
    if (!text || text.trim().length === 0) return false;

    // Clean the text
    const cleanText = text
      .trim()
      .toLowerCase()
      .replace(/[:\s*]+$/, ""); // Remove trailing colons, spaces, asterisks

    // Question patterns (should NOT be hidden) - more comprehensive
    const questionPatterns = [
      /\?/,
      /\bwhat\b/,
      /\bhow\b/,
      /\bwhen\b/,
      /\bwhere\b/,
      /\bwhy\b/,
      /\bwhich\b/,
      /\bwho\b/,
      /\bdo you\b/,
      /\bare you\b/,
      /\bhave you\b/,
      /\bwill you\b/,
      /\bcan you\b/,
      /\bplease\b/,
      /\bchoose\b/,
      /\bselect\b/,
      /\bdescribe\b/,
      /\bexplain\b/,
      /\bprovide\b/,
      /\bspecify\b/,
      /\bindicate\b/,
      /\bcheck\b.*\bif\b/,
      /\bmark\b.*\bthat\b/,
      /\blist\b/,
      /\benter\b.*\bdetail/,
      /\btell\b.*\babout/,
    ];

    if (questionPatterns.some((pattern) => pattern.test(cleanText))) {
      return false;
    }

    // Simple field patterns (should be hidden) - more comprehensive
    const simpleFieldPatterns = [
      /^(first\s+name|last\s+name|full\s+name|name)$/,
      /^(email\s+address|email)$/,
      /^(phone\s+number|phone|telephone|cell|mobile)$/,
      /^(address|street\s+address|home\s+address|mailing\s+address)$/,
      /^(address\s+line\s+[12])$/,
      /^(city|town)$/,
      /^(state|province)$/,
      /^(zip\s+code|postal\s+code|zip)$/,
      /^(country)$/,
      /^(date\s+of\s+birth|birth\s+date|dob|date)$/,
      /^(ssn|social\s+security\s+number)$/,
      /^(age)$/,
      /^(gender|sex)$/,
      /^(occupation|job\s+title|title)$/,
      /^(company|employer|organization)$/,
      /^(signature|sign\s+here)$/,
      /^(initial|initials)$/,
      /^(amount|price|cost|fee)$/,
      /^(notes|comments)$/,
    ];

    return simpleFieldPatterns.some((pattern) => pattern.test(cleanText));
  };

  // Method 1: Immediately hide all probable simple field labels
  const hideLabelsImmediately = () => {
    const labels = document.querySelectorAll(".form-label.d-inline");

    labels.forEach((label) => {
      const labelElement = label as HTMLElement;
      const text = labelElement.textContent || "";

      // Skip if this is clearly a protected label
      const hasProtectedClass =
        labelElement.classList.contains("question-label") ||
        labelElement.classList.contains("input-radio-label") ||
        labelElement.classList.contains("input-checkbox-label") ||
        labelElement.className.includes("radio") ||
        labelElement.className.includes("checkbox") ||
        labelElement.className.includes("question");

      if (hasProtectedClass) {
        // Ensure protected labels are visible
        labelElement.classList.add("question-label");
        return;
      }

      // Check if it's a simple field and hide immediately
      if (isSimpleFieldText(text)) {
        labelElement.classList.add(
          "simple-field-label",
          "js-hide-simple-field"
        );
        labelElement.setAttribute("data-field-type", "simple");
        // Apply immediate hiding styles
        labelElement.style.setProperty("opacity", "0", "important");
        labelElement.style.setProperty("height", "0", "important");
        labelElement.style.setProperty("margin", "0", "important");
        labelElement.style.setProperty("padding", "0", "important");
        labelElement.style.setProperty("overflow", "hidden", "important");
        labelElement.style.setProperty("visibility", "hidden", "important");
        labelElement.style.setProperty("display", "none", "important");
      } else {
        // It's likely a question - ensure it's visible
        labelElement.classList.add("question-label");
      }
    });
  };

  // Run immediately
  hideLabelsImmediately();

  // Method 2: Early DOM ready execution
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", hideLabelsImmediately);
  }

  // Method 3: Continuous monitoring for dynamic content
  const observer = new MutationObserver((mutations) => {
    let shouldRerun = false;

    mutations.forEach((mutation) => {
      if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
        // Check if any form labels were added
        for (let i = 0; i < mutation.addedNodes.length; i++) {
          const node = mutation.addedNodes[i];
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as Element;
            if (
              element.classList?.contains("form-label") ||
              element.querySelector?.(".form-label.d-inline")
            ) {
              shouldRerun = true;
              break;
            }
          }
        }
      }
    });

    if (shouldRerun) {
      // Run immediately, no delay
      hideLabelsImmediately();
    }
  });

  // Start observing immediately
  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  return observer;
}

// Execute critical hiding as early as possible
const labelObserver = criticalLabelHiding();

// Additional safety net - run after React renders
setTimeout(() => {
  const labels = document.querySelectorAll(
    ".form-label.d-inline:not([data-field-type])"
  );
  if (labels.length > 0) {
    console.log(
      "Running safety net label hiding for",
      labels.length,
      "missed labels"
    );
    criticalLabelHiding();
  }
}, 0);
